import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Download, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { certificationAssignmentService } from '@/services/certification-assignment.service';

interface CertificationBulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

interface ValidationResult {
  row_number: number;
  valid: boolean;
  errors: string[];
}

export default function CertificationBulkImportModal({ isOpen, onClose, onImportSuccess }: CertificationBulkImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string>('');
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validationResults, setValidationResults] = useState<ValidationResult[] | null>(null);
  const [showSample, setShowSample] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sampleCSV = `type,identifier,certification_slug,deadline_days,is_mandatory,message,send_email_notification,enable_automatic_reminder,reminder_frequency_days
USER,john@example.com,aws-solutions-architect,30,true,Please complete,true,true,7
USER,jane@example.com,google-cloud-professional,45,false,Optional cert,true,false,
GROUP,engineering,kubernetes-administrator,60,true,,true,true,14
GROUP,devops,docker-certified,30,true,DevOps required,true,true,7
ROLE,engineering(manager),project-management,90,true,Important!,true,false,`;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setValidationResults(null);

      // Read file content
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setCsvContent(content);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleValidate = async () => {
    if (!csvContent) return;

    setIsValidating(true);
    setValidationResults(null);

    try {
      // Call actual validation API
      const result = await certificationAssignmentService.validateBulkImport({
        csv_content: csvContent,
        is_base64: false
      });

      // Set validation results from API response
      setValidationResults(result.validation_results);

      const invalidCount = result.invalid_count;
      const validCount = result.valid_count;

      if (invalidCount > 0) {
        toast.error(`Found errors in ${invalidCount} row(s). Please check the validation results below.`);
      } else {
        toast.success(`All ${validCount} row(s) are valid and ready for import`);
      }
    } catch (error: any) {
      console.error('Validation failed:', error);

      // Extract error message from API response
      let errorMessage = 'Failed to validate CSV file';

      if (error?.response?.data?.error?.error_description) {
        errorMessage = error.response.data.error.error_description;
      } else if (error?.response?.data?.description) {
        errorMessage = error.response.data.description;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!csvContent) return;

    const loadingToast = toast.loading('Importing certification assignments...');
    setIsImporting(true);

    try {
      // Call actual bulk import API
      const result = await certificationAssignmentService.bulkImport({
        csv_content: csvContent,
        is_base64: false
      });

      toast.dismiss(loadingToast);

      // Show results based on API response
      const successCount = result.successful_records;
      const failedCount = result.failed_records;
      const totalCount = result.total_records;

      if (successCount > 0 && failedCount === 0) {
        toast.success(`Successfully imported all ${successCount} certification assignments!`);
      } else if (successCount > 0 && failedCount > 0) {
        toast.success(`Imported ${successCount} of ${totalCount} certification assignments. ${failedCount} failed. Check import history for details.`, {
          duration: 6000
        });
      } else if (failedCount > 0) {
        toast.error(`All ${failedCount} certification assignments failed to import. Check import history for details.`, {
          duration: 6000
        });
      }

      // Display detailed results if available
      if (result.results && result.results.length > 0) {
        const failedResults = result.results.filter(r => r.status === 'failed');
        if (failedResults.length > 0 && failedResults.length <= 3) {
          // Show first few errors
          failedResults.slice(0, 3).forEach(r => {
            toast.error(`Row ${r.row_number}: ${r.error}`, { duration: 5000 });
          });
        }
      }

      // Close modal and refresh data
      onImportSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Import failed:', error);
      toast.dismiss(loadingToast);

      // Extract error message from API response
      let errorMessage = 'Failed to import certification assignments';

      if (error?.response?.data?.error?.error_description) {
        errorMessage = error.response.data.error.error_description;
      } else if (error?.response?.data?.description) {
        errorMessage = error.response.data.description;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'certification_assignment_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetForm = () => {
    setFile(null);
    setCsvContent('');
    setValidationResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const hasValidationErrors = validationResults && validationResults.some(r => !r.valid);
  const canImport = csvContent && !hasValidationErrors;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 common-modal-overlay">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Bulk Import Certification Assignments</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-2">How to bulk import certification assignments:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Download the CSV template or create your own following the format below</li>
                  <li>Fill in the assignment details (type, identifier, certification_slug, etc.)</li>
                  <li>Upload the CSV file and click "Validate" to check for errors</li>
                  <li>Review validation results and fix any errors</li>
                  <li>Click "Import" to create the certification assignments</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Sample CSV Format */}
          {showSample && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Sample CSV Format</h3>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-gray-600 whitespace-pre">{sampleCSV}</pre>
              </div>
              <div className="mt-3 text-xs text-gray-500">
                <p className="font-semibold mb-1">Column Descriptions:</p>
                <ul className="space-y-0.5">
                  <li><strong>type:</strong> USER, GROUP, or ROLE</li>
                  <li><strong>identifier:</strong> Email for USER, group_key for GROUP, group_key(role_key) for ROLE (e.g., engineering(manager))</li>
                  <li><strong>certification_slug:</strong> The slug/URL identifier of the certification to assign</li>
                  <li><strong>deadline_days:</strong> Number of days from import date (e.g., 7, 30, 90)</li>
                  <li><strong>is_mandatory:</strong> true or false (optional, defaults to false)</li>
                  <li><strong>message:</strong> Optional message for assignees</li>
                  <li><strong>send_email_notification:</strong> true or false - Send email notification to assignees (optional, defaults to true)</li>
                  <li><strong>enable_automatic_reminder:</strong> true or false - Enable automatic reminders (optional, defaults to false)</li>
                  <li><strong>reminder_frequency_days:</strong> Days between reminders (e.g., 7, 14). Required if enable_automatic_reminder is true</li>
                </ul>
              </div>
            </div>
          )}

          {/* File Upload */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Upload CSV File</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              {!file ? (
                <div>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop your CSV file here, or
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                  >
                    Choose File
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-gray-400" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    onClick={resetForm}
                    className="ml-4 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Validation Results */}
          {validationResults && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Validation Results</h3>
              <div className={`border rounded-lg p-4 ${hasValidationErrors ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                {hasValidationErrors ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <p className="text-sm font-semibold text-red-800">
                        Found errors in {validationResults.filter(r => !r.valid).length} row(s)
                      </p>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {validationResults.filter(r => !r.valid).map((result) => (
                        <div key={result.row_number} className="text-sm text-red-700">
                          <p className="font-medium">Row {result.row_number}:</p>
                          <ul className="list-disc list-inside ml-4">
                            {result.errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-800">
                      All {validationResults.length} row(s) are valid and ready for import
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={() => setShowSample(!showSample)}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            {showSample ? 'Hide' : 'Show'} Sample Format
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleValidate}
              disabled={!csvContent || isValidating}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidating ? 'Validating...' : 'Validate'}
            </button>
            <button
              onClick={handleImport}
              disabled={!canImport || isImporting}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? 'Importing...' : 'Import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
